import { useEffect } from "react";

import { Spinner } from "@shopify/polaris";
import { CustomerAccountsVersion } from "@smartrr/shared/shopifyGraphQL/api";

import { GettingStartedStoreAccess } from "../../libs/store";
import { CustomerPortalStep } from "../../libs/GettingStartedChecklist/CustomerPortalStep";

import SetupStep from "../SetupStep";
import { LegacyCustomerAccountsSetup } from "./LegacyCustomerAccountsSetup";
import { NewCustomerAccountsSetup } from "./NewCustomerAccountsSetup";
import themeGateway from "@vendor-app/gateways/ValidateThemeFile";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";

export default function InstallPortal() {
  const setup = GettingStartedStoreAccess.useSetup();
  const checklist = GettingStartedStoreAccess.useChecklist();
  const isLoading = GettingStartedStoreAccess.useLoading("customerPortal");
  const chosenThemeId = GettingStartedStoreAccess.useChosenThemeId();
  const { setIsAccountFileValid, update } = GettingStartedStoreAccess.useActions();
  const { isAccountFileValid } = GettingStartedStoreAccess.useThemeValidation();
  const { addToast } = useToast();

  const isNewCustomerAccounts = setup?.customerAccountsVersion === CustomerAccountsVersion.NewCustomerAccounts;

  const isStepCompleted = isNewCustomerAccounts
    ? !!setup?.onSiteSetup.customerAccountPortalManualSetup?.completed
    : !!setup?.onSiteSetup.smartrrSnippets?.completed;

  // Without a chosen theme there's no account file to check, so legacy shops are asked to pick a theme first
  const shouldShowNewCustomerAccountsSetup =
    isNewCustomerAccounts || (chosenThemeId !== "" && !isAccountFileValid);

  useEffect(() => {
    if (chosenThemeId === "") {
      return;
    }

    void (async () => {
      try {
        const themeFile = await themeGateway.getFile(chosenThemeId, "templates/customers/account.json");
        const isValid = Boolean(themeFile);

        setIsAccountFileValid(isValid);

        if (!isNewCustomerAccounts && isValid) {
          const accountSnippetFile = await themeGateway.getFile(chosenThemeId, "snippets/smartrr-account.liquid");
          const isAccountSnippetFileDetected = Boolean(accountSnippetFile);

          update(
            [{ path: "onSiteSetup.smartrrSnippets.completed", val: isAccountSnippetFileDetected }],
            "customerPortal"
          );
        }
      } catch {
        addToast("Unable to verify theme file.");
      }
    })();
  }, [chosenThemeId, isNewCustomerAccounts]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <SetupStep
      title="Install Smartrr Customer Portal"
      description="Allow customers to manage their own subscriptions through a branded portal."
      id="install-portal"
      statusBadgeTestId="install-portal-status-badge"
      isStepCompleted={isStepCompleted}
      isOpen={!!checklist?.isOpen(CustomerPortalStep)}
    >
      {shouldShowNewCustomerAccountsSetup ? (
        <NewCustomerAccountsSetup isAccountFileValid={isAccountFileValid} />
      ) : (
        <LegacyCustomerAccountsSetup />
      )}
    </SetupStep>
  );
}
